import { useEffect } from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import Page from 'components/Page';
import { PATHS } from 'routes/paths';
import { vacancyStore } from 'stores/vacancyStore';
import { useVacancyStats } from 'hooks/useVacancyStats';
import VacancyStatsDashboard from 'components/VacancyStats';
import VacancyStatsSkeleton from 'components/VacancyStats/VacancyStatsSkeleton';
import { Overline } from 'components/VacancyStats/shared';

const ERROR_ICON: Record<string, string> = {
  forbidden: 'mdi:lock-outline',
  notFound: 'mdi:file-search-outline',
  generic: 'mdi:alert-circle-outline'
};

const VacancyStatsPage = () => {
  const navigate = useNavigate();
  const { vacancyId } = useParams<{ vacancyId: string }>();
  const { data, loading, error, refetch } = useVacancyStats(vacancyId);

  // Подтягиваем саму вакансию (нужна для списка навыков на дашборде);
  // на статистику не влияет, поэтому ошибки тут глушим.
  useEffect(() => {
    if (vacancyId) {
      vacancyStore.getVacancyById(vacancyId).catch(() => undefined);
    }
  }, [vacancyId]);

  const goToVacancy = () => {
    if (vacancyId) {
      navigate(PATHS.hr.vacancy.replace(':id', vacancyId));
    } else {
      navigate(PATHS.hr.company);
    }
  };

  return (
    <Stack sx={{ height: '100%', overflow: 'auto', pr: 2 }} spacing={3}>
      {/* <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: 1 }}
      >
        <Box>
          <Overline>Дашборд</Overline>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'grey.900' }}>
            Статистика по вакансии
          </Typography>
        </Box>
        {data && !loading && (
          <Tooltip title="Обновить">
            <IconButton onClick={refetch} aria-label="Обновить статистику">
              <Icon icon="mdi:refresh" width={22} height={22} />
            </IconButton>
          </Tooltip>
        )}
      </Stack> */}

      {loading && <VacancyStatsSkeleton />}

      {!loading && error && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{
            py: 8,
            px: 3,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'grey.300'
          }}
        >
          <Icon icon={ERROR_ICON[error.kind]} width={56} height={56} color="#919EAB" />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'grey.900' }}>
            {error.message}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            {error.kind === 'forbidden'
              ? 'Похоже, у вас нет прав на просмотр аналитики этой вакансии.'
              : error.kind === 'notFound'
              ? 'Проверьте ссылку — возможно, вакансия была удалена.'
              : 'Попробуйте обновить страницу или повторить запрос.'}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            {error.kind === 'generic' && (
              <Button
                variant="contained"
                startIcon={<Icon icon="mdi:refresh" />}
                onClick={refetch}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
              >
                Повторить
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Icon icon="mdi:arrow-left" />}
              onClick={goToVacancy}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1 }}
            >
              К вакансии
            </Button>
          </Stack>
        </Stack>
      )}

      {!loading && !error && data && <VacancyStatsDashboard stats={data} />}
    </Stack>
  );
};

export default VacancyStatsPage;
